import { isGroupDetails } from "@custom-types/type-guards";
import { createSelector } from "@reduxjs/toolkit";
import { groupsAdapter } from "@store/groups/groups-slice";
/**
 * Returns all the stored groups
 */
export const groupsSelector = createSelector(state => state.groups, groups => {
  return groupsAdapter.getSelectors().selectAll(groups);
});

/**
 * Returns all the stored groups filtered by search text.
 */
export const filteredCompanyGroupsSelector = createSelector(state => state.groups, state => state.ui.search.debouncedSearchText, (groups, searchText) => {
  const searchTextLower = searchText.toLowerCase();
  const allGroups = groupsAdapter.getSelectors().selectAll(groups);
  return allGroups.filter(group => {
    return group.name.toLowerCase().includes(searchTextLower);
  }).sort((groupA, groupB) => {
    // Sort by creation date, descending
    return groupB.createdAt - groupA.createdAt;
  });
});

/**
 * Returns the fetching properties of the groups slice.
 */
export const fetchingGroupsFlagsSelector = createSelector(state => state.groups, groups => {
  return groups.fetching;
});

/**
 * Returns true if a given group is currently ongoing a process.
 *
 * @param groupId
 */
export function isGroupProcessingSelector(groupId) {
  return createSelector(state => state.groups, groups => {
    return groups.fetching.processingGroups[groupId];
  });
}

/**
 * Gets a group entity by providing the group ID
 */
export function getGroupByIdSelector(groupId) {
  return createSelector(state => state.groups, groups => {
    if (!groupId) {
      return undefined;
    }
    return groupsAdapter.getSelectors().selectById(groups, groupId) ?? undefined;
  });
}

/**
 * Get the selected group using the selected group ID and finding it on the store,
 * returns null if there's no groupId or if the group could not be found in the store.
 */
export const selectedGroupSelector = createSelector(state => state.groups, groups => {
  const selectedGroupId = groups.selectedGroupId;
  if (!selectedGroupId) {
    return null;
  }
  return groupsAdapter.getSelectors().selectById(groups, selectedGroupId) ?? null;
});

/** Returns the members of selected group */
export const selectedGroupMembersSelector = createSelector(state => state.groups, selectedGroupSelector, (groups, selectedGroup) => {
  if (!groups.selectedGroupId) {
    return [];
  }
  if (selectedGroup && isGroupDetails(selectedGroup)) {
    return selectedGroup.members;
  } else {
    return [];
  }
});

/**
 * Returns how many projects a group holds, archived ones included, or undefined as long as no
 * successful fetchGroupProjectsCount has delivered it, which includes a refresh still running.
 */
export function groupProjectsCountSelector(groupId) {
  return createSelector(state => state.groups, groups => groups.projectsCount[groupId]);
}